import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Plus, Loader2, Trash2, Pencil, X, Phone, ListTodo, Mail, Upload, Download, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const pwd = () => sessionStorage.getItem('utmost_admin_token') || '';

const call = async (path, { method = 'GET', body } = {}) => {
  const headers = { 'x-admin-password': pwd() };
  if (body) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API_URL}/api/timetrack${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Failed (${res.status})`); }
  return res.status === 204 ? null : res.json();
};

const PRIORITY_STYLES = {
  high: 'bg-rose-100 text-rose-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};
const STATUS_LABEL = { todo: 'To-do', in_progress: 'In progress', done: 'Done' };

/* ============================ TASKS (admin) ============================ */
export const AdminTasksView = ({ staff }) => {
  const [staffId, setStaffId] = useState('');
  const [tasks, setTasks] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { title: '', description: '', priority: 'medium', due_date: '', status: 'todo' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (sid) => {
    setLoading(true); setErr('');
    try { setTasks(await call(`/admin/tasks${sid ? `?staff_id=${sid}` : ''}`)); }
    catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(staffId); }, [staffId, load]);

  const staffName = (id) => staff.find((s) => s.id === id)?.name || '—';

  const openAdd = () => { setEditing(null); setForm({ ...empty }); setShowForm(true); };
  const openEdit = (t) => { setEditing(t); setForm({ title: t.title, description: t.description, priority: t.priority, due_date: t.due_date || '', status: t.status }); setShowForm(true); };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      if (editing) {
        await call(`/admin/tasks/${editing.id}`, { method: 'PATCH', body: form });
      } else {
        const targetStaff = staffId || form.staff_id;
        if (!targetStaff) { setErr('Select a staff member to assign this task to.'); setSaving(false); return; }
        await call('/admin/tasks', { method: 'POST', body: { ...form, staff_id: targetStaff } });
      }
      setShowForm(false); load(staffId);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const remove = async (t) => { if (!window.confirm('Delete this task?')) return; try { await call(`/admin/tasks/${t.id}`, { method: 'DELETE' }); load(staffId); } catch (e) { setErr(e.message); } };

  return (
    <div className="space-y-3" data-testid="tt-tasks-view">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="tt-tasks-staff-filter">
          <option value="">All staff</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <Button size="sm" onClick={openAdd} className="bg-[#0A2A43] text-white" data-testid="tt-add-task-btn"><Plus className="h-4 w-4 mr-1" /> Assign task</Button>
      </div>
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
      {loading ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#0A2A43]" /></div> : (
        <>
          {tasks.length === 0 && <Card><CardContent className="p-6 text-center text-gray-500 text-sm">No tasks yet. Assign one to get started.</CardContent></Card>}
          {tasks.map((t) => (
            <Card key={t.id} data-testid={`tt-task-${t.id}`}><CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-medium ${PRIORITY_STYLES[t.priority]}`}>{t.priority}</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{STATUS_LABEL[t.status]}</span>
                    <p className="font-semibold text-[#0A2A43]">{t.title}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Assigned to <b>{staffName(t.staff_id)}</b>{t.due_date ? ` · due ${t.due_date}` : ''}</p>
                  {t.description && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{t.description}</p>}
                  {t.notes && <p className="text-xs text-gray-500 mt-1 bg-gray-50 border rounded p-2 whitespace-pre-wrap"><b>VA note:</b> {t.notes}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`tt-edit-task-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => remove(t)} data-testid={`tt-delete-task-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </CardContent></Card>
          ))}
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="tt-task-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-[#0A2A43]">{editing ? 'Edit task' : 'Assign task'}</h3><button onClick={() => setShowForm(false)}><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              {!editing && !staffId && (
                <select value={form.staff_id || ''} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="tt-task-staff">
                  <option value="">Assign to…</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
              <input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="tt-task-title" />
              <textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="tt-task-desc" />
              <div className="flex gap-2">
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="flex-1 px-3 py-2 border rounded-lg text-sm bg-white" data-testid="tt-task-priority">
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
                <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="tt-task-due" />
              </div>
              {editing && (
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="tt-task-status">
                  <option value="todo">To-do</option><option value="in_progress">In progress</option><option value="done">Done</option>
                </select>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#0A2A43] text-white" data-testid="tt-task-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} {editing ? 'Save' : 'Assign'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ==================== EMAIL SETTINGS (admin) ==================== */
const EmailSettings = () => {
  const [recipients, setRecipients] = useState([]);
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { call('/admin/summary-recipients').then((d) => { setRecipients(d.recipients || []); setInput((d.recipients || []).join(', ')); }).catch(() => {}); }, []);

  const saveRecipients = async () => {
    setBusy(true); setMsg('');
    try {
      const list = input.split(',').map((s) => s.trim()).filter(Boolean);
      const d = await call('/admin/summary-recipients', { method: 'POST', body: { recipients: list } });
      setRecipients(d.recipients || []); setInput((d.recipients || []).join(', ')); setMsg('Recipients saved.');
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const sendNow = async (which) => {
    setBusy(true); setMsg('');
    try {
      const d = await call(`/admin/${which}/send-now`, { method: 'POST' });
      setMsg(which === 'summary' ? `Summary sent to ${d.sent}/${d.total || 0} recipient(s).` : `Reminders sent to ${d.sent} VA(s).`);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  return (
    <Card className="border-dashed" data-testid="tt-email-settings">
      <CardContent className="p-4">
        <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left" data-testid="tt-email-settings-toggle">
          <span className="flex items-center gap-2 text-sm font-semibold text-[#0A2A43]"><Mail className="h-4 w-4" /> Automated email settings</span>
          <span className="text-xs text-gray-400">{open ? 'Hide' : 'Show'}</span>
        </button>
        {open && (
          <div className="mt-3 space-y-3">
            <p className="text-xs text-gray-500">
              <b>Task reminders</b> email each VA at 8:00 AM ET with their overdue / due-today / due-tomorrow tasks.
              The <b>Daily VA Summary</b> emails the recipients below at 7:00 PM CST with each VA's completed tasks and calls logged.
            </p>
            <div>
              <label className="text-xs font-medium text-gray-600">Daily summary recipients (comma-separated)</label>
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="admin@utmostfinancial.ca" className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" data-testid="tt-summary-recipients-input" />
              <p className="text-[11px] text-gray-400 mt-1">Current: {recipients.join(', ') || 'none'}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={saveRecipients} disabled={busy} className="bg-[#0A2A43] text-white" data-testid="tt-save-recipients">Save recipients</Button>
              <Button size="sm" variant="outline" onClick={() => sendNow('summary')} disabled={busy} data-testid="tt-send-summary">Send summary now</Button>
              <Button size="sm" variant="outline" onClick={() => sendNow('reminders')} disabled={busy} data-testid="tt-send-reminders">Send reminders now</Button>
            </div>
            {msg && <p className="text-xs text-gray-600" data-testid="tt-email-settings-msg">{msg}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ============================ CONTACTS (admin) ============================ */
export const AdminContactsView = ({ staff }) => {
  const [staffId, setStaffId] = useState('');
  const [contacts, setContacts] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { first_name: '', last_name: '', cell_phone: '', main_phone: '', email: '', company: '', address: '', city: '', postal_code: '', notes: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  // Bulk upload state
  const [showBulk, setShowBulk] = useState(false);
  const [bulkStaff, setBulkStaff] = useState('');
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkParsed, setBulkParsed] = useState(null); // null = not parsed yet
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkErr, setBulkErr] = useState('');

  const load = useCallback(async (sid) => {
    setLoading(true); setErr('');
    try { setContacts(await call(`/admin/contacts${sid ? `?staff_id=${sid}` : ''}`)); }
    catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(staffId); }, [staffId, load]);

  const staffName = (id) => staff.find((s) => s.id === id)?.name || '—';
  const openAdd = () => { setEditing(null); setForm({ ...empty }); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    const parts = (c.name || '').trim().split(' ');
    setForm({
      first_name: c.first_name || parts.slice(0, -1).join(' ') || parts[0] || '',
      last_name: c.last_name || (parts.length > 1 ? parts.slice(-1).join(' ') : ''),
      cell_phone: c.cell_phone || c.phone || '',
      main_phone: c.main_phone || '',
      email: c.email || '', company: c.company || '',
      address: c.address || '', city: c.city || '', postal_code: c.postal_code || '',
      notes: c.notes || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      if (editing) {
        await call(`/admin/contacts/${editing.id}`, { method: 'PATCH', body: form });
      } else {
        const targetStaff = staffId || form.staff_id;
        if (!targetStaff) { setErr('Select a staff member for this contact.'); setSaving(false); return; }
        await call('/admin/contacts', { method: 'POST', body: { ...form, staff_id: targetStaff } });
      }
      setShowForm(false); load(staffId);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const remove = async (c) => { if (!window.confirm(`Delete ${c.name}?`)) return; try { await call(`/admin/contacts/${c.id}`, { method: 'DELETE' }); load(staffId); } catch (e) { setErr(e.message); } };

  // ---- Bulk upload ----
  const openBulk = () => {
    setBulkStaff(staffId || '');
    setBulkFileName(''); setBulkParsed(null); setBulkErr(''); setShowBulk(true);
  };

  const downloadTemplate = () => {
    const csv = 'Name,Phone,Email,Company,Notes\nJane Doe,416-555-1234,jane@example.com,Acme Inc,Prefers evening calls\n';
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'call-list-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkFile = async (fileList) => {
    const f = fileList && fileList[0];
    if (!f) return;
    setBulkFileName(f.name); setBulkErr(''); setBulkParsed(null); setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`${API_URL}/api/timetrack/admin/contacts/bulk-parse`, {
        method: 'POST', headers: { 'x-admin-password': pwd() }, body: fd,
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Failed (${res.status})`); }
      const data = await res.json();
      setBulkParsed(data.contacts || []);
      if (!data.contacts || data.contacts.length === 0) setBulkErr('No contacts with a name and phone number were found in that file.');
    } catch (e) { setBulkErr(e.message); } finally { setBulkBusy(false); }
  };

  const confirmBulkImport = async () => {
    const targetStaff = bulkStaff || staffId;
    if (!targetStaff) { setBulkErr('Please choose which VA these contacts belong to.'); return; }
    if (!bulkParsed || bulkParsed.length === 0) return;
    setBulkBusy(true); setBulkErr('');
    try {
      const r = await call('/admin/contacts/bulk-import', { method: 'POST', body: { staff_id: targetStaff, contacts: bulkParsed } });
      setShowBulk(false);
      setStaffId(targetStaff);
      load(targetStaff);
      window.alert(`Imported ${r.imported} contact(s) successfully.`);
    } catch (e) { setBulkErr(e.message); } finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="tt-contacts-view">
      <EmailSettings />
      <div className="flex items-center gap-2 flex-wrap">
        <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="tt-contacts-staff-filter">
          <option value="">All staff</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <Button size="sm" onClick={openAdd} className="bg-[#0A2A43] text-white" data-testid="tt-add-contact-btn"><Plus className="h-4 w-4 mr-1" /> Add contact</Button>
        <Button size="sm" variant="outline" onClick={openBulk} data-testid="tt-bulk-upload-btn"><Upload className="h-4 w-4 mr-1" /> Bulk upload</Button>
      </div>
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
      {loading ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#0A2A43]" /></div> : (
        <>
          {contacts.length === 0 && <Card><CardContent className="p-6 text-center text-gray-500 text-sm">No contacts yet.</CardContent></Card>}
          {contacts.map((c) => (
            <Card key={c.id} data-testid={`tt-contact-${c.id}`}><CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-semibold text-[#0A2A43] flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {c.name} <span className="text-gray-500 font-normal">· {c.cell_phone || c.phone}</span></p>
                <p className="text-xs text-gray-500">For <b>{staffName(c.staff_id)}</b>{c.main_phone && c.main_phone !== (c.cell_phone || c.phone) ? ` · Main: ${c.main_phone}` : ''}{c.company ? ` · ${c.company}` : ''}{c.email ? ` · ${c.email}` : ''}</p>
                {(c.address || c.city || c.postal_code) && <p className="text-xs text-gray-500">{[c.address, c.city, c.postal_code].filter(Boolean).join(', ')}</p>}
                {c.notes && <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{c.notes}</p>}
                {(c.call_logs || []).length > 0 && <p className="text-[11px] text-gray-400 mt-1">{c.call_logs.length} call log(s) · last: {c.call_logs[0].outcome}</p>}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`tt-edit-contact-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => remove(c)} data-testid={`tt-delete-contact-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent></Card>
          ))}
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="tt-contact-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-[#0A2A43]">{editing ? 'Edit contact' : 'Add contact'}</h3><button onClick={() => setShowForm(false)}><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              {!editing && !staffId && (
                <select value={form.staff_id || ''} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="tt-contact-staff">
                  <option value="">Add to whose list…</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="First Name *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="tt-contact-first-name" />
                <input placeholder="Last Name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="tt-contact-last-name" />
                <input placeholder="Cell Phone *" value={form.cell_phone} onChange={(e) => setForm({ ...form, cell_phone: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="tt-contact-cell-phone" />
                <input placeholder="Main Phone" value={form.main_phone} onChange={(e) => setForm({ ...form, main_phone: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="tt-contact-main-phone" />
                <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="tt-contact-email" />
                <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="tt-contact-company" />
                <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="px-3 py-2 border rounded-lg text-sm col-span-2" data-testid="tt-contact-address" />
                <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="tt-contact-city" />
                <input placeholder="Postal Code" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="tt-contact-postal" />
              </div>
              <textarea placeholder="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="tt-contact-notes" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#0A2A43] text-white" data-testid="tt-contact-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} {editing ? 'Save' : 'Add'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk upload modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowBulk(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tt-bulk-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#0A2A43] flex items-center gap-2"><Upload className="h-5 w-5" /> Bulk upload contacts</h3>
              <button onClick={() => setShowBulk(false)}><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Add to which VA's list? <span className="text-rose-500">*</span></label>
                <select value={bulkStaff} onChange={(e) => setBulkStaff(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="tt-bulk-staff">
                  <option value="">Choose a VA…</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <FileText className="h-6 w-6 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-2">Upload a <b>CSV</b>, <b>Excel (.xlsx)</b> or <b>PDF</b> file.</p>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,.pdf,text/csv,application/pdf"
                  onChange={(e) => handleBulkFile(e.target.files)}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#0A2A43] file:text-white file:cursor-pointer"
                  data-testid="tt-bulk-file"
                />
                {bulkFileName && <p className="text-xs text-gray-500 mt-2">{bulkFileName}</p>}
                <button onClick={downloadTemplate} className="text-xs text-[#0077B6] hover:underline mt-3 inline-flex items-center gap-1" data-testid="tt-bulk-template"><Download className="h-3 w-3" /> Download CSV template</button>
                <p className="text-[11px] text-gray-400 mt-1">PDFs are read with AI. Columns detected: Name, Phone, Email, Company, Notes. Name & Phone are required.</p>
              </div>

              {bulkBusy && <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-3"><Loader2 className="h-4 w-4 animate-spin" /> {bulkParsed === null ? 'Reading file…' : 'Importing…'}</div>}
              {bulkErr && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="tt-bulk-error">{bulkErr}</div>}

              {bulkParsed && bulkParsed.length > 0 && (
                <div data-testid="tt-bulk-preview">
                  <p className="text-sm font-medium text-[#0A2A43] mb-2">{bulkParsed.length} contact(s) found — review before importing:</p>
                  <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                    {bulkParsed.map((c, i) => (
                      <div key={i} className="p-2 text-sm">
                        <span className="font-medium text-[#0A2A43]">{c.name}</span> <span className="text-gray-500">· {c.phone}</span>
                        {(c.company || c.email) && <span className="text-xs text-gray-400 block">{[c.company, c.email].filter(Boolean).join(' · ')}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowBulk(false)}>Cancel</Button>
              <Button
                onClick={confirmBulkImport}
                disabled={bulkBusy || !bulkParsed || bulkParsed.length === 0}
                className="bg-[#0A2A43] text-white"
                data-testid="tt-bulk-import-btn"
              >
                {bulkBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Import {bulkParsed && bulkParsed.length > 0 ? `${bulkParsed.length} contact(s)` : ''}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminTasksIcon = ListTodo;
